import React from 'react';
import {
    Box,
    Button,
    Popover,
    Slider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    IconButton
} from "@mui/material";
import { Close } from '@material-ui/icons';
import { tUi } from '../../../../../../../utils/dq_lc';

const TTSControlPopover = ({
    tts,
    anchorEl = tts?.ttsSettingsAnchor,
    open = Boolean(tts?.ttsSettingsAnchor),
    onClose = tts ? () => tts.setTtsSettingsAnchor(null) : undefined,
    availableVoices = tts?.availableVoices ?? [],
    selectedVoiceURI = tts?.selectedVoiceURI ?? '',
    onSelectVoice = tts?.setSelectedVoiceURI,
    ttsRate = tts?.ttsRate ?? 1,
    onChangeRate = tts?.setTtsRate,
    ttsPitch = tts?.ttsPitch ?? 1,
    onChangePitch = tts?.setTtsPitch,
    ttsVolume = tts?.ttsVolume ?? 1,
    onChangeVolume = tts?.setTtsVolume,
    onResetDefaults = tts ? () => {
        tts.setTtsRate(1);
        tts.setTtsPitch(1);
        tts.setTtsVolume(1);
    } : undefined,
    uiLocale = 'en-US',
}) => {
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{
                sx: {
                    p: 2,
                    minWidth: 320,
                    maxWidth: 360,
                    borderRadius: 3,
                    background:
                        'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(245, 245, 250, 0.65) 50%, rgba(240, 240, 255, 0.6) 100%)',
                    backdropFilter: 'blur(16px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                    border: '1px solid rgba(229, 231, 235, 0.4)',
                    boxShadow: `0 8px 30px rgba(0, 0, 0, 0.15),
                               0 4px 12px rgba(0, 0, 0, 0.1),
                               inset 0 1px 0 rgba(255, 255, 255, 0.6)`,
                    zIndex: 2000,
                },
            }}
        >
            <Box sx={{ position: 'relative' }}>
                {/* Header row */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography
                        variant="h6"
                        style={{
                            fontWeight: 700,
                            color: '#ED6628',
                        }}
                    >
                        Voice Settings
                    </Typography>

                    <IconButton
                        size="small"
                        onClick={onClose}
                        style={{
                            fontWeight: 'bold',
                            color: '#ED6628',
                            border: 'none',
                            '&:hover': { background: '#ED6628', color: '#fff' },
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Box>

                {/* Voice select */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel id="voice-select-label">{tUi('preview.tts.voice', uiLocale)}</InputLabel>
                    <Select
                        labelId="voice-select-label"
                        label={tUi('preview.tts.voice', uiLocale)}
                        value={selectedVoiceURI || ''}
                        onChange={(e) => onSelectVoice && onSelectVoice(e.target.value)}
                    >
                        {availableVoices.length === 0 && (
                            <MenuItem value="" disabled>
                                {tUi('preview.tts.noVoices', uiLocale)}
                            </MenuItem>
                        )}
                        {availableVoices.map((v) => (
                            <MenuItem key={v.voiceURI} value={v.voiceURI}>
                                {v.name} ({v.lang}){v.default ? ' • default' : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Rate Slider */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                        {tUi('preview.tts.rate', uiLocale)}: {ttsRate.toFixed(2)}
                    </Typography>
                    <Slider
                        size="small"
                        value={ttsRate}
                        min={0.5}
                        max={1.75}
                        step={0.05}
                        onChange={(_, v) => onChangeRate && onChangeRate(Array.isArray(v) ? v[0] : v)}
                        valueLabelDisplay="auto"
                        sx={{
                            color: 'transparent',
                            height: 6,
                            borderRadius: 4,
                            '& .MuiSlider-track': {
                                background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                                border: 'none',
                            },
                            '& .MuiSlider-rail': {
                                background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                            },
                            '& .MuiSlider-thumb': {
                                width: 16,
                                height: 16,
                                backgroundColor: '#fff',
                                border: '2px solid #ee6d29',
                                boxShadow: '0 0 2px rgba(0,0,0,0.3)',
                            },
                            '& .MuiSlider-valueLabel': {
                                background: '#ee6d29',
                                color: '#fff',
                                borderRadius: '6px',
                            },
                        }}
                    />
                </Box>

                {/* Pitch Slider */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                        {tUi('preview.tts.pitch', uiLocale)}: {ttsPitch.toFixed(2)}
                    </Typography>
                    <Slider
                        size="small"
                        value={ttsPitch}
                        min={0.5}
                        max={2}
                        step={0.05}
                        onChange={(_, v) => onChangePitch && onChangePitch(Array.isArray(v) ? v[0] : v)}
                        valueLabelDisplay="auto"
                        sx={{
                            color: 'transparent',
                            height: 6,
                            borderRadius: 4,
                            '& .MuiSlider-track': {
                                background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                                border: 'none',
                            },
                            '& .MuiSlider-rail': {
                                background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                            },
                            '& .MuiSlider-thumb': {
                                width: 16,
                                height: 16,
                                backgroundColor: '#fff',
                                border: '2px solid #ee6d29',
                                boxShadow: '0 0 2px rgba(0,0,0,0.3)',
                            },
                            '& .MuiSlider-valueLabel': {
                                background: '#ee6d29',
                                color: '#fff',
                                borderRadius: '6px',
                            },
                        }}
                    />
                </Box>

                {/* Volume Slider */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                        {tUi('preview.tts.volume', uiLocale)}: {ttsVolume.toFixed(2)}
                    </Typography>
                    <Slider
                        size="small"
                        value={ttsVolume}
                        min={0}
                        max={1}
                        step={0.05}
                        onChange={(_, v) => onChangeVolume && onChangeVolume(Array.isArray(v) ? v[0] : v)}
                        valueLabelDisplay="auto"
                        sx={{
                            color: 'transparent',
                            height: 6,
                            borderRadius: 4,
                            '& .MuiSlider-track': {
                                background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                                border: 'none',
                            },
                            '& .MuiSlider-rail': {
                                background: 'linear-gradient(90deg, #ee6d29, #f59e0b)',
                            },
                            '& .MuiSlider-thumb': {
                                width: 16,
                                height: 16,
                                backgroundColor: '#fff',
                                border: '2px solid #ee6d29',
                                boxShadow: '0 0 2px rgba(0,0,0,0.3)',
                            },
                            '& .MuiSlider-valueLabel': {
                                background: '#ee6d29',
                                color: '#fff',
                                borderRadius: '6px',
                            },
                        }}
                    />
                </Box>

                {/* Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button
                        size="small"
                        onClick={onResetDefaults}
                        sx={{
                            color: '#ED6628',
                            border: '1px solid #ED6628',
                            '&:hover': {
                                backgroundColor: '#ED6628',
                                borderColor: '#ED6628',
                                color: '#fff',
                            },
                        }}
                    >
                        Reset defaults
                    </Button>
                    <Button
                        size="small"
                        sx={{
                            color: '#ED6628',
                            border: '1px solid #ED6628',
                            '&:hover': {
                                backgroundColor: '#ED6628',
                                borderColor: '#ED6628',
                                color: '#fff',
                            },
                        }}
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </Box>
            </Box>
        </Popover>
    );
};

export default TTSControlPopover;
